import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';
import { AsyncRequestStatus } from '@portal/shared/store/request-status';

import { DateUtil } from '@portal/shared/utils/date-util';
import { DeviceDetailsMetricsActions } from '../../../store/device-details-metrics.actions';
import * as DeviceDetailsMetricsReducer from '../../../store/device-details-metrics.reducer';
import { MeterConsumptionTabContentActions } from './meter-consumption-tab-content.actions';
import * as MeterConsumptionTabContentReducer from './meter-consumption-tab-content.reducer';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { MeterConsumptionTabContentService } from '../meter-consumption-tab-content.service';
import { mapTableSortToPayloadSort } from '@portal/shared/components/listing-page/helpers/map-sort-descriptor-to-sorting-state.helper';
import {
  GetChartDataPayload,
  GetFiltersPayload,
  GetTableDataItem,
  GetTableDataPayload,
  MeterConsumptionFieldName
} from '../meter-consumption-tab-content.types';
import { ExportRow } from '@portal/shared/types/export.types';
import { ListingPageModel } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListResponse } from '@portal/shared/types/api-response.types';
import { ListingPayload } from '@portal/shared/types/listing.types';
import { Commodity } from '@portal/shared/types/commodity.types';
import { DEVICE_DETAILS_METRICS } from '../../../device-details-metrics.types';
import { DatepickerRange } from '@ui/aos-lib';
import { DeviceDetailsPage } from '../../../../../devices.models';
import { FlowDirection } from '@portal/shared/types/filter.types';
import { ExportUtilsService } from 'src/app/services/utility/export-utils.service';
import { ExportFacadeService } from 'src/app/services/utility/export-facade.service';

function fetchFilters(
  tzService: TimezoneService,
  meterConsumptionTabContentService: MeterConsumptionTabContentService,
  dateRange: DatepickerRange,
  deviceDetails: DeviceDetailsPage
): Observable<Action> {
  const { start, end } = tzService.getDatepickerRange({
    start: dateRange.start,
    end: dateRange.end
  });
  const payload: GetFiltersPayload = {
    deviceUID: deviceDetails.deviceUID as string,
    endDateUtc: DateUtil.addOneDayToString(end),
    startDateUtc: DateUtil.toISOString(start),
    ...(deviceDetails.accountNumber ? { accountNumber: deviceDetails.accountNumber } : {}),
    ...(deviceDetails.accountSubNumber ? { accountSubNumber: deviceDetails.accountSubNumber } : {})
  };
  return meterConsumptionTabContentService.getFilters(payload).pipe(
    map(filters => MeterConsumptionTabContentActions.loadFiltersSuccess({ filters })),
    catchError(() => of(MeterConsumptionTabContentActions.loadFiltersFailure({ error: 'Failed to load filters' })))
  );
}

export const loadFilters$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService)
  ) =>
    actions$.pipe(
      ofType(DeviceDetailsMetricsActions.setDateRange),
      withLatestFrom(
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails),
        store.select(DeviceDetailsMetricsReducer.selectActiveMetricsTab)
      ),
      filter(
        ([, dateRange, deviceDetails, activeTab]) =>
          dateRange !== null &&
          deviceDetails !== null &&
          deviceDetails.commodity === Commodity.Electricity &&
          activeTab === DEVICE_DETAILS_METRICS.Consumption
      ),
      exhaustMap(([, dateRange, deviceDetails]) =>
        fetchFilters(tzService, meterConsumptionTabContentService, dateRange!, deviceDetails!).pipe(
          startWith(MeterConsumptionTabContentActions.loadFilters())
        )
      )
    ),
  { functional: true }
);

export const loadFiltersOnMetricsTabSwitch$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService)
  ) =>
    actions$.pipe(
      ofType(DeviceDetailsMetricsActions.setActiveMetricsTab),
      filter(({ activeMetricsTab }) => activeMetricsTab === DEVICE_DETAILS_METRICS.Consumption),
      withLatestFrom(
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails)
      ),
      filter(
        ([, dateRange, deviceDetails]) =>
          dateRange !== null && deviceDetails !== null && deviceDetails.commodity === Commodity.Electricity
      ),
      exhaustMap(([, dateRange, deviceDetails]) =>
        fetchFilters(tzService, meterConsumptionTabContentService, dateRange!, deviceDetails!)
      )
    ),
  { functional: true }
);

export const loadData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(
        MeterConsumptionTabContentActions.filterChanged,
        MeterConsumptionTabContentActions.loadFiltersSuccess,
        MeterConsumptionTabContentActions.tableStateChanged
      ),
      withLatestFrom(store.select(MeterConsumptionTabContentReducer.selectShouldShowChart)),
      switchMap(([, shouldShowChart]) => {
        return shouldShowChart
          ? of(MeterConsumptionTabContentActions.loadChartData())
          : of(MeterConsumptionTabContentActions.loadTableData());
      })
    ),
  { functional: true }
);

export const loadChartData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService)
  ) =>
    actions$.pipe(
      ofType(MeterConsumptionTabContentActions.loadChartData),
      withLatestFrom(
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails),
        store.select(MeterConsumptionTabContentReducer.selectPayload)
      ),
      filter(([, dateRange, deviceDetails]) => dateRange !== null && deviceDetails !== null),
      switchMap(([, dateRange, deviceDetails, payload]) => {
        const { start, end } = tzService.getDatepickerRange({
          start: dateRange!.start,
          end: dateRange!.end
        });
        const updatedPayload: GetChartDataPayload = {
          filters: [
            {
              filterType: MeterConsumptionFieldName.dateTimeUTC,
              filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
            },
            {
              filterType: MeterConsumptionFieldName.deviceUID,
              filteredValues: [deviceDetails?.deviceUID as string]
            },
            ...(deviceDetails?.accountNumber
              ? [{ filterType: MeterConsumptionFieldName.accountNumber, filteredValues: [deviceDetails.accountNumber] }]
              : []),
            ...(deviceDetails?.accountSubNumber
              ? [
                  {
                    filterType: MeterConsumptionFieldName.accountSubNumber,
                    filteredValues: [deviceDetails.accountSubNumber]
                  }
                ]
              : []),
            ...(payload?.filters ?? [])
          ]
        };

        const flowDirectionFilter = updatedPayload.filters.find(
          f => f.filterType === MeterConsumptionFieldName.flowDirection
        );
        if (!flowDirectionFilter?.filteredValues.length) {
          return of(
            MeterConsumptionTabContentActions.loadChartDataSuccess({
              healthData: []
            })
          );
        }

        return meterConsumptionTabContentService.getChartData(updatedPayload).pipe(
          map(healthData =>
            MeterConsumptionTabContentActions.loadChartDataSuccess({
              healthData: healthData.filter(hd =>
                flowDirectionFilter?.filteredValues.map(fv => fv.toLowerCase()).includes(hd.type)
              )
            })
          ),
          catchError(() =>
            of(MeterConsumptionTabContentActions.loadChartDataFailure({ error: 'Failed to load chart data' }))
          )
        );
      })
    ),
  { functional: true }
);

export const loadDataOnTabSwitch$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(MeterConsumptionTabContentActions.selectTabType),
      withLatestFrom(
        store.select(MeterConsumptionTabContentReducer.selectShouldShowChart),
        store.select(MeterConsumptionTabContentReducer.selectChartDataRequest),
        store.select(MeterConsumptionTabContentReducer.selectTableDataRequest)
      ),
      switchMap(([, shouldShowChart, chartStatus, tableStatus]) => {
        if (shouldShowChart && chartStatus.status === AsyncRequestStatus.Idle) {
          return of(MeterConsumptionTabContentActions.loadChartData());
        }
        if (!shouldShowChart && tableStatus.status === AsyncRequestStatus.Idle) {
          return of(MeterConsumptionTabContentActions.loadTableData());
        }
        return EMPTY;
      })
    ),
  { functional: true }
);

export const loadTableData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService)
  ) =>
    actions$.pipe(
      ofType(MeterConsumptionTabContentActions.loadTableData),
      withLatestFrom(
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails),
        store.select(MeterConsumptionTabContentReducer.selectPayload),
        store.select(MeterConsumptionTabContentReducer.selectTableState)
      ),
      filter(([, dateRange, deviceDetails]) => dateRange !== null && deviceDetails !== null),
      switchMap(([, dateRange, deviceDetails, payload, tableState]) => {
        const { start, end } = tzService.getDatepickerRange({
          start: dateRange!.start,
          end: dateRange!.end
        });
        const { skip, take, sort } = tableState;

        const sortDescriptor = sort?.[0] ?? { field: 'date', dir: 'desc' as const };
        const updatedPayload: GetTableDataPayload = {
          filters: [
            {
              filterType: MeterConsumptionFieldName.dateTimeUTC,
              filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
            },
            {
              filterType: MeterConsumptionFieldName.deviceUID,
              filteredValues: [deviceDetails?.deviceUID as string]
            },
            ...(deviceDetails?.accountNumber
              ? [{ filterType: MeterConsumptionFieldName.accountNumber, filteredValues: [deviceDetails.accountNumber] }]
              : []),
            ...(deviceDetails?.accountSubNumber
              ? [
                  {
                    filterType: MeterConsumptionFieldName.accountSubNumber,
                    filteredValues: [deviceDetails.accountSubNumber]
                  }
                ]
              : []),
            ...(payload?.filters ?? [])
          ],
          sort: {
            columnName: 'DateTimeUTC',
            orderDirection: mapTableSortToPayloadSort(sortDescriptor)
          },
          skip,
          take
        };

        const flowDirectionFilter = payload?.filters.find(
          f => f.filterType === MeterConsumptionFieldName.flowDirection
        );
        if (!flowDirectionFilter?.filteredValues.length) {
          return of(
            MeterConsumptionTabContentActions.loadTableDataSuccess({
              tableData: { data: [], total: 0 }
            })
          );
        }

        return meterConsumptionTabContentService.getTableData(updatedPayload).pipe(
          map(tableData =>
            MeterConsumptionTabContentActions.loadTableDataSuccess({
              tableData: {
                data: tableData.items,
                total: tableData.totalCount
              }
            })
          ),
          catchError(() =>
            of(MeterConsumptionTabContentActions.loadTableDataFailure({ error: 'Failed to load table data' }))
          )
        );
      })
    ),
  { functional: true }
);

export const export$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    tzService = inject(TimezoneService),
    meterConsumptionTabContentService = inject(MeterConsumptionTabContentService),
    exportUtils = inject(ExportUtilsService),
    exportFacade = inject(ExportFacadeService)
  ) =>
    actions$.pipe(
      ofType(MeterConsumptionTabContentActions.exportFile),
      withLatestFrom(
        store.select(DeviceDetailsMetricsReducer.selectDateRange),
        store.select(DeviceDetailsMetricsReducer.selectDeviceDetails),
        store.select(MeterConsumptionTabContentReducer.selectPayload),
        store.select(MeterConsumptionTabContentReducer.selectTableState),
        store.select(MeterConsumptionTabContentReducer.selectUom)
      ),
      filter(([, dateRange, deviceDetails]) => dateRange !== null && deviceDetails !== null),
      switchMap(([{ fileType }, dateRange, deviceDetails, payload, tableState, uom]) => {
        const { start, end } = tzService.getDatepickerRange({ start: dateRange!.start, end: dateRange!.end });

        const sortDescriptor = tableState.sort?.[0] ?? { field: 'date', dir: 'desc' as const };
        const exportPayload: GetTableDataPayload = {
          filters: [
            {
              filterType: MeterConsumptionFieldName.dateTimeUTC,
              filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
            },
            { filterType: MeterConsumptionFieldName.deviceUID, filteredValues: [deviceDetails!.deviceUID as string] },
            ...(deviceDetails?.accountNumber
              ? [{ filterType: MeterConsumptionFieldName.accountNumber, filteredValues: [deviceDetails.accountNumber] }]
              : []),
            ...(deviceDetails?.accountSubNumber
              ? [
                  {
                    filterType: MeterConsumptionFieldName.accountSubNumber,
                    filteredValues: [deviceDetails.accountSubNumber]
                  }
                ]
              : []),
            ...(payload?.filters ?? [])
          ],
          sort: { columnName: 'DateTimeUTC', orderDirection: mapTableSortToPayloadSort(sortDescriptor) }
        };

        const flowFilter = exportPayload.filters.find(f => f.filterType === MeterConsumptionFieldName.flowDirection);
        const visibleDirections = (
          flowFilter?.filteredValues?.length
            ? flowFilter.filteredValues
            : [FlowDirection.Forward, FlowDirection.Reverse]
        ) as FlowDirection[];

        const withUom = (label: string): string => (uom ? `${label} (${uom})` : label);
        const directionTitleMap: Record<FlowDirection, string> = {
          [FlowDirection.Forward]: withUom('Forward'),
          [FlowDirection.Reverse]: withUom('Reverse'),
          [FlowDirection.Net]: withUom('Net'),
          [FlowDirection.Total]: withUom('Total')
        };

        const serialNumber = deviceDetails!.serialNumber ?? deviceDetails!.deviceUID;
        const fileName = `Export-ConsumptionMetrics-${serialNumber}`;

        const listingPageModel = {
          dataSource: {
            getAll: (state: GetTableDataPayload) => meterConsumptionTabContentService.getTableData(state),
            total: 0
          },
          exportConfig: {
            fileName,
            mapData: (data: ListResponse<GetTableDataItem>): ExportRow[] => {
              return data.items.map(item => {
                const row: ExportRow = {
                  date: { title: 'Date/Time', value: item.date }
                };
                for (const dir of visibleDirections) {
                  row[dir] = {
                    title: directionTitleMap[dir],
                    value: item[dir.toLowerCase() as keyof GetTableDataItem] ?? ''
                  };
                }
                return row;
              });
            }
          }
        } as Pick<ListingPageModel, 'dataSource' | 'exportConfig'>;

        return meterConsumptionTabContentService.getTableData({ ...exportPayload, skip: 0, take: 1 }).pipe(
          switchMap(({ totalCount }) =>
            exportUtils.getTableDataForExport(
              exportPayload as unknown as ListingPayload,
              listingPageModel as ListingPageModel,
              totalCount
            )
          ),
          tap(data => {
            // getTableDataForExport emits caught errors as data, so only arrays are exportable rows
            if (!Array.isArray(data)) {
              throw new Error('Failed to export');
            }
            exportFacade.exportRows(fileType, data as ExportRow[], null, null, fileName);
          }),
          map(() => MeterConsumptionTabContentActions.exportFileSuccess()),
          catchError(() => of(MeterConsumptionTabContentActions.exportFileFailure({ error: 'Failed to export CSV' })))
        );
      })
    ),
  { functional: true }
);
